import { useRef, useState, useEffect, useCallback } from "react"
import { DocumentLoadOverlay } from "components/ui/customWebviewer/DocumentLoadOverlay/DocumentLoadOverlay"
import { Modal } from "components/ui/modals/Modal/Modal"
import { useWebviewer } from "hooks/webviewer.hook"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { AIAgent } from "./layout/AIAget/AIAgent"
import { AIAgentViewerIcon } from "components/ui/icons/iaAgent/AIAgentViewerIcon"
import { Splitter } from "components/ui/splitter/Splitter"
import { useChatStore } from "store/aiAgent.store"
import { useViewerStore } from "store/viewer.store"
import { FeedbackPanel } from "components/ui/FeedbackPanel/FeedbackPanel"
import { useSearchParams } from "react-router-dom"
import type { TDrawingStatus } from "types/drawer.type"
import style from "./DocumentModal.module.scss"

const {
	container,
	pdfViewerContainer,
	aiAgentButton,
	aiAgentContainer,
	aiAgentContainerOpen,
	feedbackPanelContainer,
	feedbackPanelContainerOpen,
	wrapper,
	splitterStyles,
} = style

type Props = {
	isFileModalOpen: boolean
	setFileModalClose: () => void
	previewLink: string | undefined
	drawingStatus: TDrawingStatus
}

export const DocumentModal = ({ isFileModalOpen, setFileModalClose, previewLink, drawingStatus }: Props) => {
	const { isChatOpen, openChat } = useChatStore()
	const { isFeedbackPanelOpen, closeFeedbackPanel } = useViewerStore()
	const [panelSizes, setPanelSizes] = useState<[string | number, string | number, string | number]>([
		"0%",
		"100%",
		"0%",
	])
	const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

	const [searchParams] = useSearchParams()
	const drawingId = Number(searchParams.get("drawingID"))
	const processingId = Number(searchParams.get("processingID"))
	const processingUrl = window.location.href

	// Use the custom hook to open file preview
	const viewer = useRef(null)
	useWebviewer({ fileLink: previewLink, previewRef: viewer, isModalOpen: isFileModalOpen })

	// Update panel sizes when chat or feedback panel opens/closes
	useEffect(() => {
		if (isFeedbackPanelOpen && isChatOpen) {
			setPanelSizes(["25%", "50%", "25%"])
		} else if (isFeedbackPanelOpen) {
			setPanelSizes(["25%", "75%", "0%"])
		} else if (isChatOpen) {
			setPanelSizes(["0%", "50%", "50%"])
		} else {
			setPanelSizes(["0%", "100%", "0%"])
		}

		// Trigger a window resize so the embedded WebViewer recomputes its layout.
		// Reuse the same debounce ref the splitter's `handleResize` callback maintains,
		// so rapid panel toggles don't stack up timeouts and unmount within 100ms
		// doesn't leak a fire-after-unmount.
		if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current)
		resizeTimeoutRef.current = setTimeout(() => {
			try {
				window.dispatchEvent(new Event("resize"))
			} catch (error) {
				console.warn("Could not trigger resize event:", error)
			}
		}, 100)
	}, [isChatOpen, isFeedbackPanelOpen])

	const closeModalHandle = () => {
		setFileModalClose()
	}

	// Debounced resize handler to update WebViewer
	const handleResize = useCallback(
		(sizes: number[] | string[] | undefined) => {
			if ((isChatOpen || isFeedbackPanelOpen) && sizes) {
				setPanelSizes(sizes as [string | number, string | number, string | number])

				// Clear previous timeout
				if (resizeTimeoutRef.current) {
					clearTimeout(resizeTimeoutRef.current)
				}

				// Debounce the WebViewer layout update
				resizeTimeoutRef.current = setTimeout(() => {
					try {
						window.dispatchEvent(new Event("resize"))
					} catch (error) {
						console.warn("Could not update WebViewer layout:", error)
					}
				}, 150)
			}
		},
		[isChatOpen, isFeedbackPanelOpen],
	)

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (resizeTimeoutRef.current) {
				clearTimeout(resizeTimeoutRef.current)
			}
		}
	}, [])

	// Always render all panels, control with size
	const panels = [
		{
			children: (
				<div
					className={`${feedbackPanelContainer} ${isFeedbackPanelOpen ? feedbackPanelContainerOpen : ""}`}
					style={{ display: isFeedbackPanelOpen ? "block" : "none" }}
				>
					{isFeedbackPanelOpen && (
						<FeedbackPanel
							onClose={closeFeedbackPanel}
							drawingId={drawingId}
							processingId={processingId}
							processingUrl={processingUrl}
							drawingStatus={drawingStatus}
						/>
					)}
				</div>
			),
			size: panelSizes[0],
			resizable: isFeedbackPanelOpen,
			min: isFeedbackPanelOpen ? 300 : 0,
			max: isFeedbackPanelOpen ? undefined : 0,
			id: "feedback-panel",
		},
		{
			children: (
				<div className={`${pdfViewerContainer}`}>
					<div ref={viewer} style={{ width: "100%", height: "100%" }}></div>
					{!isChatOpen && (
						<ButtonPrimary onClick={openChat} className={aiAgentButton}>
							<AIAgentViewerIcon />
							AI Agent
						</ButtonPrimary>
					)}
				</div>
			),
			size: panelSizes[1],
			resizable: isChatOpen || isFeedbackPanelOpen,
			min: isChatOpen || isFeedbackPanelOpen ? 400 : 0,
			id: "pdf-viewer",
		},
		{
			children: (
				<div
					className={`${aiAgentContainer} ${isChatOpen ? aiAgentContainerOpen : ""}`}
					style={{ display: isChatOpen ? "block" : "none" }}
				>
					{isChatOpen && <AIAgent />}
				</div>
			),
			size: panelSizes[2],
			resizable: isChatOpen,
			min: isChatOpen ? 400 : 0,
			max: isChatOpen ? undefined : 0,
			id: "ai-agent",
		},
	]

	return (
		<Modal isModalOpen={isFileModalOpen} setModalClose={closeModalHandle} width="100%">
			{/* The download progress this viewer used to get from Apryse, which it loses now that the
			    bytes come from the shared document cache instead of a URL. Portals to document.body,
			    so its position in this tree does not matter. */}
			<DocumentLoadOverlay />
			<div className={container}>
				<div className={wrapper}>
					<Splitter layout="horizontal" panels={panels} onResize={handleResize} className={splitterStyles} />
				</div>
			</div>
		</Modal>
	)
}
